// 3rd party
import * as React from 'react';
import { observer } from 'mobx-react';
import { Controller, useForm } from 'react-hook-form';

// Shared UI
import { LocalizedText, useLocalization } from '@shared-ui/localization-context';

// UI Toolkit
import { UitkPrimaryButton, UitkSecondaryButton } from 'uitk-react-button';
import { UitkInformativeInput, UitkNumberInput, UitkSelect } from 'uitk-react-input';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkRadio, UitkRadioGroup } from 'uitk-react-switch';
import { UitkHeading } from 'uitk-react-text';

// Components
import { ExpediaPackageWidgetCodeMarkup, ExpediaPackageWidgetPreviewFrame } from 'components/package';
import {
  getInitialTrackingState,
  initialDestinationState,
  initialFixedHeightState,
  initialFixedWidthState,
  initialHeightStates,
  initialWidthStates,
  initialLinkState,
  initialStayState,
} from 'components/package/expedia/config';
import { Destination, DimensionSizing, DimensionUnit, LinkOpen, StayDuration } from 'components/package/expedia/enums';
import {
  DestinationState,
  HeightState,
  LinkState,
  StayState,
  TrackingState,
  WidgetLinksState,
  WidgetStylesState,
  WidthState,
} from 'components/package/expedia/interfaces';
import { MoreInformationDialog, MoreInformationDialogTriggerButton } from 'components/shared/more-information';
import { WidgetCode, WidgetPreview } from 'components/shared/widget';

// Hooks
import { useStores } from 'hooks';

// Models
import { LinkType } from 'models/analytics/enums';
import { LinkAnalyticsMicroMessageContent } from 'models/analytics';
import { ProductV2 } from 'models';

// Utils
import {
  analyticsEvent,
  getLinkAnalyticsMicroMessage,
  getProgramAnalyticsMicroMessage,
  getProductAnalyticsMicroMessage,
  getUserAnalyticsMicroMessage,
} from '../../util/analytics';

interface ExpediaPackageV2Props {
  product?: ProductV2;
}

interface ExpediaPackageV2State
  extends TrackingState,
    StayState,
    DestinationState,
    WidthState,
    HeightState,
    LinkState {}

const ExpediaPackageV2 = observer(
  // tslint:disable-next-line:no-shadowed-variable
  function ExpediaPackageV2({ product }: ExpediaPackageV2Props): JSX.Element {
    const l10n = useLocalization();
    const { analytics, userAuth, user, pzStore } = useStores();
    const { activeProgram } = user;

    const initialState = React.useMemo(
      function getInitialState(): ExpediaPackageV2State {
        return {
          ...getInitialTrackingState(activeProgram),
          ...initialStayState,
          ...initialDestinationState,
          ...initialFixedWidthState,
          ...initialFixedHeightState,
          ...initialLinkState,
        };
      },
      [activeProgram],
    );

    const buildLinkAnalyticsMicroMessageContent = (linkTargetUrl: string): LinkAnalyticsMicroMessageContent => ({
      linkType: LinkType.CUSTOM,
      linkIndex: 0,
      linkTargetUrl,
    });

    const analyticsTrackEvents = (rfrr: string) => {
      const microMessages = [
        getProgramAnalyticsMicroMessage(activeProgram, activeProgram?.programStatus),
        getProductAnalyticsMicroMessage(product),
      ];

      const filterAnalyticsTrackEvent = {
        'affhub.product_config': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Product Config Presented', microMessages);
        },
        'affhub_copy_code.selected': () => {
          const linkAnalyticsMicroMessageContent = buildLinkAnalyticsMicroMessageContent(widgetCodeMarkupLink);
          const microMessagesWithLink = [
            ...microMessages,
            getLinkAnalyticsMicroMessage(linkAnalyticsMicroMessageContent),
          ];

          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Copy Code Selected', microMessagesWithLink);
        },
        'affhub_code_create.selected': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Code Create Selected', microMessages);
        },
        'affhub_code_create.succeeded': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Code Create Succeeded', microMessages);
        },
        'affhub_code_create.failed': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Code Create Failed', microMessages);
        },
        'affhub_implementation_guide.selected': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Implementation Guide Selected', microMessages);
        },
      };

      filterAnalyticsTrackEvent[rfrr]();
    };

    const [state, setState] = React.useState(initialState);

    const {
      control,
      formState: { errors },
      handleSubmit,
      reset,
      setValue,
      register,
      getValues,
      clearErrors,
    } = useForm<ExpediaPackageV2State>({ mode: 'onBlur', defaultValues: initialState });

    const specialChars = ['>', '<', '&', "'", '"'];

    const onFormValid = (formState: ExpediaPackageV2State) => {
      analyticsTrackEvents('affhub_code_create.succeeded');

      setState(formState);
    };

    // tslint:disable-next-line:no-shadowed-variable
    const onFormInvalid = (errors) => {
      // analytics
      analyticsTrackEvents('affhub_code_create.failed');
    };

    React.useEffect(() => {
      // Page load analytics
      analyticsTrackEvents('affhub.product_config');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFormReset = (event) => {
      event.preventDefault();

      reset(initialState);
      setState(initialState);
    };

    const onSubmit = handleSubmit(onFormValid, onFormInvalid);

    const { widgetPreviewFrameLink, widgetCodeMarkupLink } = React.useMemo(
      function getWidgetLinks(): WidgetLinksState {
        const {
          trackingType,
          trackingInformationPZCamref,
          trackingInformationPZPubref,
          stayDuration,
          destination,
          widthSize,
          widthSizing,
          heightSize,
          heightSizing,
          linkOpen,
        } = state;

        const origin = 'https://www.expedia.com';
        const widgetsPathname = '/marketing/widgets';
        const widgetPathname = '/packagedeals/widget';
        const widgetCodePathname = widgetPathname;
        // const widgetPreviewPathname = '/packagedeals/preview';
        const widgetPreviewPathname = widgetPathname;

        const widgetSearchParams = new URLSearchParams({
          wtt: trackingType,
          tp1: trackingInformationPZCamref,
          tp2: trackingInformationPZPubref,
          des: destination,
          los: stayDuration,
          wws: linkOpen,
          dws: `${widthSizing}${widthSize}${heightSizing}${heightSize}`,
        });

        const widgetSearch = `?${decodeURIComponent(`${widgetSearchParams}`)}`;

        return {
          widgetPreviewFrameLink: `${origin}${widgetsPathname}${widgetPreviewPathname}${widgetSearch}`,
          widgetCodeMarkupLink: `${origin}${widgetsPathname}${widgetCodePathname}${widgetSearch}`,
        };
      },
      [state],
    );

    const { widgetPreviewFrameStyles, widgetCodeMarkupStyles } = React.useMemo(
      function getWidgetStyles(): WidgetStylesState {
        const { widthSize, widthUnit, heightSizing, heightSize, heightUnit } = state;

        const { heightSizeMax: initialFixedHeightSizeMax } = initialFixedHeightState;

        const widgetBaseWidthStyle = `${widthSize}${widthUnit}`;
        const widgetBaseHeightStyle = `${heightSize}${heightUnit}`;

        const widgetFakeHeightStyle =
          heightSizing === DimensionSizing.RELATIVE &&
          `${(heightSize / 100) * initialFixedHeightSizeMax}${DimensionUnit.FIXED}`;

        return {
          widgetPreviewFrameStyles: {
            width: widgetBaseWidthStyle,
            height: widgetFakeHeightStyle || widgetBaseHeightStyle,
          },
          widgetCodeMarkupStyles: `width:${widgetBaseWidthStyle};height:${widgetBaseHeightStyle};`,
        };
      },
      [state],
    );

    return (
      <article>
        <UitkLayoutGrid columns={{ large: 2 }} space="twelve">
          <UitkLayoutGridItem>
            <div>
              <section>
                <form name={'expediaPackage'} onSubmit={onSubmit} onReset={handleFormReset} noValidate={true}>
                  <UitkLayoutGrid space="six">
                    <UitkLayoutGridItem>
                      <div>
                        <fieldset>
                          <UitkSpacing margin={{ blockend: 'four' }}>
                            <legend>
                              <UitkHeading tag="h2" size={4}>
                                <LocalizedText message="product.form.tracking-information.pz.heading.partnerize_tracking_information" />
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          <UitkLayoutGrid space="four">
                            <UitkLayoutGridItem>
                              <div>
                                <input type="hidden" {...register('trackingType')} />
                                <input type="hidden" {...register('trackingInformationPZCamref')} />
                                <Controller
                                  control={control}
                                  name="trackingInformationPZPubref"
                                  rules={{
                                    validate: (pubref) => {
                                      if (new RegExp(`[${specialChars.join('')}]+`).test(pubref)) {
                                        return l10n.formatText(
                                          'product.form.error.special_chars_invalid',
                                          specialChars.join(' '),
                                        );
                                      }
                                    },
                                  }}
                                  render={({ field: { onChange, onBlur, value, name } }) => (
                                    <UitkInformativeInput
                                      id="tracking-pubref"
                                      name={name}
                                      label={l10n.formatText(
                                        'product.form.tracking-information.pz.label.pubref_optional',
                                      )}
                                      informativeTrigger={<MoreInformationDialogTriggerButton id="pz-pubref" />}
                                      a11yInformativeTrigger={l10n.formatText(
                                        'more-information.dialog.trigger.more_information',
                                      )}
                                      value={value}
                                      onChange={({ target: { value: newValue } }) => {
                                        onChange(newValue);
                                      }}
                                      onBlur={onBlur}
                                      invalid={errors.trackingInformationPZPubref?.message}
                                    />
                                  )}
                                />

                                <MoreInformationDialog
                                  id="pz-pubref"
                                  title={l10n.formatText('more-information.dialog.pz-pubref.title.what_pubref')}
                                  description={l10n.formatText(
                                    'more-information.dialog.pz-pubref.description.pubref_optional_reference_include_tracking_link_for_your_reporting_purposes_pubref_tracks_clicks_through_bookings',
                                  )}
                                />
                              </div>
                            </UitkLayoutGridItem>
                          </UitkLayoutGrid>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>

                    <UitkLayoutGridItem>
                      <div>
                        <fieldset>
                          <UitkSpacing margin={{ blockend: 'four' }}>
                            <legend>
                              <UitkHeading tag="h2" size={4}>
                                <LocalizedText message="product.form.configuration.title" />
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          <UitkLayoutGrid space="four">
                            <UitkLayoutGridItem>
                              <div>
                                <Controller
                                  control={control}
                                  name="stayDuration"
                                  render={({ field: { onChange, onBlur, value, name } }) => (
                                    <UitkSelect
                                      id="stayDuration"
                                      name={name}
                                      label={l10n.formatText('product.form.stay-duration.label.length_stay')}
                                      value={value}
                                      onChange={({ target: { value: newValue } }) => {
                                        onChange(newValue);
                                      }}
                                      onBlur={onBlur}
                                    >
                                      <option value={StayDuration.TWO_DAYS}>
                                        {l10n.formatText('product.expedia-package.form.stay-duration.label.two_days')}
                                      </option>
                                      <option value={StayDuration.THREE_DAYS}>
                                        {l10n.formatText('product.expedia-package.form.stay-duration.label.three_days')}
                                      </option>
                                      <option value={StayDuration.FIVE_DAYS}>
                                        {l10n.formatText('product.expedia-package.form.stay-duration.label.five_days')}
                                      </option>
                                      <option value={StayDuration.SEVEN_DAYS}>
                                        {l10n.formatText('product.expedia-package.form.stay-duration.label.seven_days')}
                                      </option>
                                      <option value={StayDuration.TEN_DAYS}>
                                        {l10n.formatText('product.expedia-package.form.stay-duration.label.ten_days')}
                                      </option>
                                      <option value={StayDuration.FOURTEEN_DAYS}>
                                        {l10n.formatText(
                                          'product.expedia-package.form.stay-duration.label.fourteen_days',
                                        )}
                                      </option>
                                    </UitkSelect>
                                  )}
                                />
                              </div>
                            </UitkLayoutGridItem>

                            <UitkLayoutGridItem>
                              <div>
                                <Controller
                                  control={control}
                                  name="destination"
                                  render={({ field: { onChange, onBlur, value, name } }) => (
                                    <UitkSelect
                                      id="destinationLabel"
                                      name={name}
                                      label={l10n.formatText('product.form.destination.label')}
                                      value={value}
                                      onChange={({ target: { value: newValue } }) => {
                                        onChange(newValue);
                                      }}
                                      onBlur={onBlur}
                                    >
                                      <option value={Destination.CUN}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.cancun')}
                                      </option>
                                      <option value={Destination.PUJ}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.punta_cana')}
                                      </option>
                                      <option value={Destination.NYC}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.new_york')}
                                      </option>
                                      <option value={Destination.MIA}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.miami')}
                                      </option>
                                      <option value={Destination.ORL}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.orlando_orl')}
                                      </option>
                                      <option value={Destination.MBJ}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.montego')}
                                      </option>
                                      <option value={Destination.LAS}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.las_vegas')}
                                      </option>
                                      <option value={Destination.SJU}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.san_juan')}
                                      </option>
                                      <option value={Destination.PVR}>
                                        {l10n.formatText(
                                          'product.expedia-package.form.destination.label.puerto_vallarta',
                                        )}
                                      </option>
                                      <option value={Destination.HNL}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.honolulu')}
                                      </option>
                                      <option value={Destination.SJD}>
                                        {l10n.formatText(
                                          'product.expedia-package.form.destination.label.san_jose_del_cabo',
                                        )}
                                      </option>
                                      <option value={Destination.PAR}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.paris')}
                                      </option>
                                      <option value={Destination.AUA}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.oranjestad')}
                                      </option>
                                      <option value={Destination.OGG}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.kahului')}
                                      </option>
                                      <option value={Destination.MCO}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.orlando_mco')}
                                      </option>
                                      <option value={Destination.MSY}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.new_orleans')}
                                      </option>
                                      <option value={Destination.LON}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.london')}
                                      </option>
                                      <option value={Destination.NAS}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.nassau')}
                                      </option>
                                      <option value={Destination.SAN}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.san_diego')}
                                      </option>
                                      <option value={Destination.CHI}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.chicago')}
                                      </option>
                                      <option value={Destination.ROM}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.rome')}
                                      </option>
                                      <option value={Destination.QSF}>
                                        {l10n.formatText(
                                          'product.expedia-package.form.destination.label.san_francisco',
                                        )}
                                      </option>
                                      <option value={Destination.LAX}>
                                        {l10n.formatText(
                                          'product.expedia-package.form.destination.label.los_angeles_lax_intl',
                                        )}
                                      </option>
                                      <option value={Destination.QLA}>
                                        {l10n.formatText(
                                          'product.expedia-package.form.destination.label.los_angeles_qla_all',
                                        )}
                                      </option>
                                      <option value={Destination.FLL}>
                                        {l10n.formatText(
                                          'product.expedia-package.form.destination.label.fort_lauderdale',
                                        )}
                                      </option>
                                      <option value={Destination.PHX}>
                                        {l10n.formatText('product.expedia-package.form.destination.label.phoenix')}
                                      </option>
                                    </UitkSelect>
                                  )}
                                />
                              </div>
                            </UitkLayoutGridItem>

                            <UitkLayoutGridItem>
                              <div>
                                <fieldset>
                                  <UitkSpacing margin={{ blockend: 'two' }}>
                                    <legend className="is-visually-hidden">
                                      <UitkHeading tag="h3" size={6}>
                                        <LocalizedText message="product.form.dimension.sizing.label.width" />
                                      </UitkHeading>
                                    </legend>
                                  </UitkSpacing>
                                  <UitkLayoutGrid columns={2} space="three">
                                    <UitkLayoutGridItem>
                                      <div>
                                        <Controller
                                          control={control}
                                          name="widthSizing"
                                          render={({ field: { onChange, onBlur, value, name } }) => (
                                            <UitkSelect
                                              id="width-sizing"
                                              name={name}
                                              label={l10n.formatText(
                                                'product.form.dimension.sizing.label.measurement_unit',
                                              )}
                                              value={value}
                                              onChange={({ target: { value: newValue } }) => {
                                                onChange(newValue);
                                                clearErrors('widthSize');

                                                const dimensionSizing = newValue as DimensionSizing;
                                                const {
                                                  widthSize,
                                                  widthSizeMin,
                                                  widthSizeMax,
                                                  widthSizing,
                                                  widthUnit,
                                                } = initialWidthStates[dimensionSizing];

                                                setValue('widthUnit', widthUnit);
                                                setValue('widthSize', widthSize);
                                                setValue('widthSizeMin', widthSizeMin);
                                                setValue('widthSizeMax', widthSizeMax);
                                                setValue('widthSizing', widthSizing);
                                              }}
                                              onBlur={onBlur}
                                            >
                                              <option value={DimensionSizing.FIXED}>
                                                {l10n.formatText('product.form.dimension.sizing.label.fixed')}
                                              </option>
                                              <option value={DimensionSizing.RELATIVE}>
                                                {l10n.formatText('product.form.dimension.sizing.label.relative')}
                                              </option>
                                            </UitkSelect>
                                          )}
                                        />
                                        {/* Width hidden inputs for react-hook-form state change */}
                                        <input type="hidden" {...register('widthUnit')} />
                                        <input type="hidden" {...register('widthSizeMin', { valueAsNumber: true })} />
                                        <input type="hidden" {...register('widthSizeMax', { valueAsNumber: true })} />
                                      </div>
                                    </UitkLayoutGridItem>

                                    <UitkLayoutGridItem>
                                      <div>
                                        <Controller
                                          control={control}
                                          name="widthSize"
                                          rules={{
                                            validate() {
                                              const widthSizing = getValues('widthSizing');
                                              const widthSize = getValues('widthSize');
                                              const widthSizeMin = getValues('widthSizeMin');
                                              const widthSizeMax = getValues('widthSizeMax');

                                              if (
                                                widthSize === undefined ||
                                                widthSize < widthSizeMin ||
                                                widthSize > widthSizeMax
                                              ) {
                                                switch (widthSizing) {
                                                  case DimensionSizing.FIXED: {
                                                    return l10n.formatText(
                                                      'product.form.dimension.size.fixed.error.provide_correct_size',
                                                      widthSizeMin,
                                                      widthSizeMax,
                                                    );
                                                  }
                                                  case DimensionSizing.RELATIVE: {
                                                    return l10n.formatText(
                                                      'product.form.dimension.size.relative.error.provide_correct_percentage',
                                                    );
                                                  }
                                                }
                                              }
                                            },
                                          }}
                                          render={({ field: { onChange, onBlur, value, name } }) => (
                                            <UitkNumberInput
                                              id="width-size"
                                              name={name}
                                              label={l10n.formatText('product.form.dimension.sizing.label.width')}
                                              value={value}
                                              onChange={({ target: { valueAsNumber: newValue } }) => {
                                                onChange(newValue || undefined);
                                              }}
                                              onBlur={onBlur}
                                              invalid={errors.widthSize?.message}
                                            />
                                          )}
                                        />
                                      </div>
                                    </UitkLayoutGridItem>
                                  </UitkLayoutGrid>
                                </fieldset>
                              </div>
                            </UitkLayoutGridItem>

                            <UitkLayoutGridItem>
                              <div>
                                <fieldset>
                                  <UitkSpacing margin={{ blockend: 'two' }}>
                                    <legend className="is-visually-hidden">
                                      <UitkHeading tag="h3" size={6}>
                                        <LocalizedText message="product.form.dimension.sizing.label.height" />
                                      </UitkHeading>
                                    </legend>
                                  </UitkSpacing>
                                  <UitkLayoutGrid columns={2} space="three">
                                    <UitkLayoutGridItem>
                                      <div>
                                        <Controller
                                          control={control}
                                          name="heightSizing"
                                          render={({ field: { onChange, onBlur, value, name } }) => (
                                            <UitkSelect
                                              id="height-sizing"
                                              name={name}
                                              label={l10n.formatText(
                                                'product.form.dimension.sizing.label.measurement_unit',
                                              )}
                                              value={value}
                                              onChange={({ target: { value: newValue } }) => {
                                                onChange(newValue);
                                                clearErrors('heightSize');

                                                const dimensionSizing = newValue as DimensionSizing;
                                                const {
                                                  heightSize,
                                                  heightSizeMin,
                                                  heightSizeMax,
                                                  heightSizing,
                                                  heightUnit,
                                                } = initialHeightStates[dimensionSizing];

                                                setValue('heightUnit', heightUnit);
                                                setValue('heightSize', heightSize);
                                                setValue('heightSizeMin', heightSizeMin);
                                                setValue('heightSizeMax', heightSizeMax);
                                                setValue('heightSizing', heightSizing);
                                              }}
                                              onBlur={onBlur}
                                            >
                                              <option value={DimensionSizing.FIXED}>
                                                {l10n.formatText('product.form.dimension.sizing.label.fixed')}
                                              </option>
                                              <option value={DimensionSizing.RELATIVE}>
                                                {l10n.formatText('product.form.dimension.sizing.label.relative')}
                                              </option>
                                            </UitkSelect>
                                          )}
                                        />
                                        {/* height hidden inputs for react-hook-form state change */}
                                        <input type="hidden" {...register('heightUnit')} />
                                        <input type="hidden" {...register('heightSizeMin', { valueAsNumber: true })} />
                                        <input type="hidden" {...register('heightSizeMax', { valueAsNumber: true })} />
                                      </div>
                                    </UitkLayoutGridItem>

                                    <UitkLayoutGridItem>
                                      <div>
                                        <Controller
                                          control={control}
                                          name="heightSize"
                                          rules={{
                                            validate() {
                                              const heightSizing = getValues('heightSizing');
                                              const heightSize = getValues('heightSize');
                                              const heightSizeMin = getValues('heightSizeMin');
                                              const heightSizeMax = getValues('heightSizeMax');

                                              if (
                                                heightSize === undefined ||
                                                heightSize < heightSizeMin ||
                                                heightSize > heightSizeMax
                                              ) {
                                                switch (heightSizing) {
                                                  case DimensionSizing.FIXED: {
                                                    return l10n.formatText(
                                                      'product.form.dimension.size.fixed.error.provide_correct_size',
                                                      heightSizeMin,
                                                      heightSizeMax,
                                                    );
                                                  }
                                                  case DimensionSizing.RELATIVE: {
                                                    return l10n.formatText(
                                                      'product.form.dimension.size.relative.error.provide_correct_percentage',
                                                    );
                                                  }
                                                }
                                              }
                                            },
                                          }}
                                          render={({ field: { onChange, onBlur, value, name } }) => (
                                            <UitkNumberInput
                                              id="height-size"
                                              name={name}
                                              label={l10n.formatText('product.form.dimension.sizing.label.height')}
                                              value={value}
                                              onChange={({ target: { valueAsNumber: newValue } }) => {
                                                onChange(newValue || undefined);
                                              }}
                                              onBlur={onBlur}
                                              invalid={errors.heightSize?.message}
                                            />
                                          )}
                                        />
                                      </div>
                                    </UitkLayoutGridItem>
                                  </UitkLayoutGrid>
                                </fieldset>
                              </div>
                            </UitkLayoutGridItem>

                            <UitkLayoutGridItem>
                              <div>
                                <fieldset>
                                  <UitkSpacing>
                                    <legend>
                                      <UitkHeading tag="h3" size={6}>
                                        <LocalizedText message="product.form.link-open.title.how_would_you_like_to_open_link" />
                                      </UitkHeading>
                                    </legend>
                                  </UitkSpacing>
                                  <Controller
                                    control={control}
                                    name="linkOpen"
                                    render={({ field: { onChange, value, name } }) => (
                                      <UitkRadioGroup
                                        group={name}
                                        selected={value}
                                        onChange={({ target: { value: newValue } }) => {
                                          onChange(newValue);
                                        }}
                                      >
                                        <UitkLayoutFlex direction="row" space="four">
                                          <UitkLayoutFlexItem>
                                            <div>
                                              <UitkRadio
                                                id="open-link-new-window"
                                                name={name}
                                                value={LinkOpen.NEW_WINDOW}
                                                checked={value === LinkOpen.NEW_WINDOW}
                                              >
                                                <LocalizedText message="product.form.link-open.label.new_window" />
                                              </UitkRadio>
                                            </div>
                                          </UitkLayoutFlexItem>
                                          <UitkLayoutFlexItem>
                                            <div>
                                              <UitkRadio
                                                id="open-link-same-window"
                                                name={name}
                                                value={LinkOpen.SAME_WINDOW}
                                                checked={value === LinkOpen.SAME_WINDOW}
                                              >
                                                <LocalizedText message="product.form.link-open.label.same_window" />
                                              </UitkRadio>
                                            </div>
                                          </UitkLayoutFlexItem>
                                        </UitkLayoutFlex>
                                      </UitkRadioGroup>
                                    )}
                                  />
                                </fieldset>
                              </div>
                            </UitkLayoutGridItem>
                          </UitkLayoutGrid>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>

                    <UitkLayoutGridItem>
                      <div>
                        <fieldset>
                          <UitkSpacing>
                            <legend className="is-visually-hidden">
                              <UitkHeading tag="h3" size={6}>
                                <LocalizedText message="product.form.actions.title" />
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          <UitkLayoutFlex direction="row" space="four">
                            <UitkLayoutFlexItem>
                              <div>
                                <UitkPrimaryButton
                                  type="submit"
                                  onClick={() => {
                                    analyticsTrackEvents('affhub_code_create.selected');
                                  }}
                                >
                                  <LocalizedText message="product.form.actions.generate_code" />
                                </UitkPrimaryButton>
                              </div>
                            </UitkLayoutFlexItem>

                            <UitkLayoutFlexItem>
                              <div>
                                <UitkSecondaryButton type="reset">
                                  <LocalizedText message="product.form.actions.reset" />
                                </UitkSecondaryButton>
                              </div>
                            </UitkLayoutFlexItem>
                          </UitkLayoutFlex>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>
                  </UitkLayoutGrid>
                </form>
              </section>
            </div>
          </UitkLayoutGridItem>

          <UitkLayoutGridItem>
            <div>
              <UitkLayoutGrid space="six">
                <UitkLayoutGridItem>
                  <div>
                    <WidgetPreview>
                      <ExpediaPackageWidgetPreviewFrame
                        link={widgetPreviewFrameLink}
                        styles={widgetPreviewFrameStyles}
                      />
                    </WidgetPreview>
                  </div>
                </UitkLayoutGridItem>

                <UitkLayoutGridItem>
                  <div>
                    <WidgetCode
                      product={product}
                      onLinkClick={() => {
                        analyticsTrackEvents('affhub_implementation_guide.selected');
                      }}
                    >
                      <ExpediaPackageWidgetCodeMarkup
                        link={widgetCodeMarkupLink}
                        styles={widgetCodeMarkupStyles}
                        onCopyClick={() => {
                          analyticsTrackEvents('affhub_copy_code.selected');
                        }}
                      />
                    </WidgetCode>
                  </div>
                </UitkLayoutGridItem>
              </UitkLayoutGrid>
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      </article>
    );
  },
);

export default ExpediaPackageV2;
export { ExpediaPackageV2 };
